import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, Mouse, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

import bridgeImage from "../assets/images/const.webp";
import highwayImage from "../assets/images/road-construction.webp";
import smartCityImage from "../assets/images/project/GRPWS.webp";

const ArchitectureLanding = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const slides = [
    {
      image: bridgeImage,
      title: "Advanced Engineering",
      subtitle: "Innovative Infrastructure",
      description:
        "Pioneering sustainable infrastructure with innovative designs that harmonize with the environment. Our engineering represent the perfection of ecological responsibility."
    },
    {
      image: highwayImage,
      title: "Road Network",
      subtitle: "Connecting Communities",
      description: "Building efficient road networks that connect communities, reduce congestion and improve safety for all road users."
    },
    {
      image: smartCityImage,
      title: "Best Drainge System",
      subtitle: "Urban Innovation",
      description: " Creating sustainable urban environments with advanced drainage systems that manage stormwater, reduce pollution and protect natural resources."
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    
    // Delayed visibility for smoother initial load
    const visibilityTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    const slideInterval = setInterval(nextSlide, 6000);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(visibilityTimer);
      clearInterval(slideInterval);
    };
  }, []);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-slate-900 overflow-hidden">
      <div className="flex flex-col lg:flex-row relative">
        {/* Main Image Display */}
        <div className="w-full lg:w-1/2 relative min-h-[50vh] lg:min-h-screen">
          <div className="absolute inset-0">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-transform duration-500 ease-out ${
                  index === activeSlide 
                    ? "translate-x-0" 
                    : index < activeSlide
                      ? "-translate-x-full"
                      : "translate-x-full"
                }`}
              >
                <div className={`absolute inset-0 transition-opacity duration-500 ${
                  index === activeSlide ? "opacity-100" : "opacity-0"
                }`}>
                  <img 
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent lg:from-slate-900/80 lg:to-transparent" />
                  {isMobile && <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900" />}
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Indicator - Desktop only */}
          {!isMobile && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden lg:flex flex-col items-center text-white/60 hover:text-white/80 transition-colors">
              <Mouse className="w-6 h-6 animate-pulse" />
              <ChevronDown className="w-4 h-4 animate-bounce" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className={`w-full lg:w-1/2 relative ${isMobile ? '-mt-20 z-10' : ''}`}>
          <div className="p-6 sm:p-8 lg:p-12">
            <div className="space-y-8 lg:space-y-12 overflow-hidden">
              {/* Header Content */}
              <div className="space-y-6 lg:space-y-8">
                <div className="space-y-3 lg:space-y-4 overflow-hidden">
                  <div className="overflow-hidden">
                    <span className={`inline-block text-amber-500 text-sm uppercase tracking-wider font-medium transition-transform duration-500 ${
                      isVisible ? "translate-y-0" : "translate-y-full"
                    }`}>
                      {slides[activeSlide].subtitle}
                    </span>
                  </div>
                  
                  <div className="overflow-hidden">
                    <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white transition-transform duration-500 delay-100 ${
                      isVisible ? "translate-x-0" : "-translate-x-full"
                    }`}>
                      Building Today
                      <span className="block text-amber-500 mt-2 transition-colors duration-300 hover:text-amber-400">
                        For a sustainable tomorrow.
                      </span>
                    </h1>
                  </div>
                </div>

                <div className="overflow-hidden">
                  <p className={`text-gray-300 text-base sm:text-lg leading-relaxed transition-transform duration-500 delay-200 ${
                    isVisible ? "translate-x-0" : "-translate-x-full"
                  }`}>
                    {slides[activeSlide].description}
                  </p>
                </div>

                <div className="overflow-hidden">
                  <div className={`transition-transform duration-500 delay-300 ${
                    isVisible ? "translate-y-0" : "translate-y-full"
                  }`}>
                    <Link to="/Contact">
                      <button className="group relative bg-amber-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg overflow-hidden transition-colors duration-300 hover:bg-amber-600">
                        <div className="relative flex items-center space-x-3">
                          <span className="text-sm sm:text-base">Begin Your Project</span>
                          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Project Showcase */}
              <div className="space-y-4 sm:space-y-6 overflow-hidden">
                <div className="flex items-center space-x-4 mb-4 sm:mb-6">
                  <h3 className="text-white text-lg sm:text-xl">Our Projects</h3>
                  <div className="h-px flex-grow bg-white/10"></div>
                </div>
                
                <div className="relative">
                  <div className="flex space-x-4 sm:space-x-6 overflow-x-auto scrollbar-hide pb-4">
                    {slides.map((slide, index) => (
                      <div
                        key={index}
                        onClick={() => setActiveSlide(index)}
                        className={`group relative flex-shrink-0 w-32 sm:w-36 lg:w-44 transition-all duration-300 ${
                          index === activeSlide
                            ? "scale-105"
                            : "scale-100 opacity-70 hover:opacity-100"
                        }`}
                      >
                        <div className={`h-24 sm:h-28 lg:h-36 rounded-lg overflow-hidden ${
                          index === activeSlide ? "ring-2 ring-amber-500" : ""
                        }`}>
                          <img 
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                        </div>
                        <p className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 text-xs sm:text-sm text-white text-center font-medium">
                          {slide.title}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Controls */}
                  <button
                    onClick={prevSlide}
                    className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-amber-500 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-amber-500 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureLanding;